import type { MutableRefObject } from 'react';
import type { DropdownProps } from '@/shared/types/dropdown';
import { warnDeprecatedPropList } from '@/modules/InnerButton/utils/deprecation';

/**
 * Props de raíz deprecadas del `Dropdown` (preferir bloques `field`, `items`, `values`, …).
 * Ver `DropdownProps` en `shared/types/dropdown.ts`.
 */
const DROPDOWN_DEPRECATED_ROOT_KEYS = [
  'label',
  'error',
  'helperText',
  'infoTooltip',
  'size',
  'color',
  'leftSlot',
  'rightSlot',
  'searchPlaceholder',
  'noResultsText',
  'noResultsDescription',
  'caret',
  'defaultValue',
  'placeholder',
  'options',
  'value',
  'valueName',
  'idName',
  'onValueChange',
  'onOptionSelect',
  'onCheckboxChange',
  'searchable',
  'maxHeight',
  'closeOnClickOutside',
  'dropdownId',
  'fullWidth',
  'auto',
  'multiple',
  'maxSelectedItems',
  'selectionSummary',
  'selectedItemsDisplay',
  'tagsOverflow',
  'tagsOverflowPopoverPlacement',
  'tagsOverflowPopoverZIndex',
  'tagsOverflowPopoverTitle',
  'preventDeselectFromOptionsList',
  'showTriggerClearButton',
  'absolute',
  'popoverZIndex',
  'footer',
  'defaultOptionSelected',
  'withCheckbox',
  'checkboxSelectionTone',
  'checkboxPosition',
  'checkboxListDensity',
  'hasCurrent',
  'onHandleAccept',
  'pill',
  'listVariant',
  'searchMultiCheckbox',
  'searchMultiCheckboxHeader',
  'searchMultiCheckboxShowSearch',
  'searchMultiCheckboxShowClearSelection',
  'onToggleChange',
  'filtersTitle',
  'tooltipProps',
  'loading',
] as const satisfies readonly (keyof DropdownProps)[];

const ALTERNATIVE =
  'API agrupada (`field`, `items`, `values`, `events`, `multi`, `popover`, `list`, …)';

/**
 * Nombres de props de raíz deprecadas que el consumidor pasó explícitamente.
 */
export function collectDeprecatedDropdownFlatKeys(props: DropdownProps): string[] {
  const used: string[] = [];
  for (const key of DROPDOWN_DEPRECATED_ROOT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      used.push(key);
    }
  }
  return used;
}

/**
 * Avisa en desarrollo si hay props planas deprecadas (una vez por firma distinta).
 */
export function warnDeprecatedDropdownFlatPropsIfNeeded(
  props: DropdownProps,
  lastSignatureRef: MutableRefObject<string>
): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const used = collectDeprecatedDropdownFlatKeys(props);
  if (used.length === 0) {
    return;
  }
  const sig = used.slice().sort().join(',');
  if (lastSignatureRef.current === sig) {
    return;
  }
  lastSignatureRef.current = sig;
  warnDeprecatedPropList(used, ALTERNATIVE, '[Dropdown]');
}
